import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import Badge from '../../components/Badge';
import Button from "../../components/Button";
import Card from "../../components/Card";
import FilterPanel from "../../components/FilterPanel";
import Input from "../../components/Input";
import PageHeader from "../../components/PageHeader";
import Table from "../../components/Table";
import { api } from "../../services/api";

function AdminSubjects() {
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingSubject, setEditingSubject] = useState(null);
  const [formData, setFormData] = useState({
    code: "",
    name: "",
    description: "",
    tags: "",
  });
  const [search, setSearch] = useState("");

  // Bulk actions state
  const [selectedSubjects, setSelectedSubjects] = useState(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);


  useEffect(() => {
    loadSubjects();
  }, [search]);

  const loadSubjects = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);

      const response = await api.get(`/admin/subjects?${params.toString()}`);
      setSubjects(response.subjects || []);
      // Clear selection when loading new data
      setSelectedSubjects(new Set());
    } catch (error) {
      toast.error("Failed to load subjects");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/subjects", {
        ...formData,
        tags: formData.tags
          .split(",")
          .map((t) => t.trim())
          .filter((t) => t),
      });
      toast.success("Subject created successfully");
      setShowCreateModal(false);
      setFormData({ code: "", name: "", description: "", tags: "" });
      loadSubjects();
    } catch (error) {
      toast.error(error.error || "Failed to create subject");
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/admin/subjects/${editingSubject._id}`, {
        ...formData,
        tags: formData.tags
          .split(",")
          .map((t) => t.trim())
          .filter((t) => t),
      });
      toast.success("Subject updated successfully");
      setEditingSubject(null);
      setFormData({ code: "", name: "", description: "", tags: "" });
      loadSubjects();
    } catch (error) {
      toast.error(error.error || "Failed to update subject");
    }
  };

  const handleDelete = async (subjectId) => {
    const result = await Swal.fire({
      title: "Delete Subject",
      text: "Are you sure you want to delete this subject? This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#d33",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;
    try {
      await api.delete(`/admin/subjects/${subjectId}`);
      toast.success("Subject deleted successfully");
      loadSubjects();
    } catch (error) {
      toast.error(error.error || "Delete failed");
    }
  };

  const startEdit = (subject) => {
    setEditingSubject(subject);
    setFormData({
      code: subject.code,
      name: subject.name,
      description: subject.description || "",
      tags: subject.tags?.join(", ") || "",
    });
  };

  // Selection handlers
  const toggleSelectAll = () => {
    if (selectedSubjects.size === subjects.length) {
      setSelectedSubjects(new Set());
    } else {
      setSelectedSubjects(new Set(subjects.map((s) => s._id)));
    }
  };

  const toggleSelectSubject = (subjectId) => {
    const newSelected = new Set(selectedSubjects);
    if (newSelected.has(subjectId)) {
      newSelected.delete(subjectId);
    } else {
      newSelected.add(subjectId);
    }
    setSelectedSubjects(newSelected);
  };

  // Bulk delete handler
  const handleBulkDelete = async () => {
    if (selectedSubjects.size === 0) {
      toast.warning("Please select at least one subject");
      return;
    }
    const result = await Swal.fire({
      title: "Bulk Delete Subjects",
      text: `Are you sure you want to delete ${selectedSubjects.size} subject${selectedSubjects.size > 1 ? "s" : ""}? This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#d33",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;
    setBulkActionLoading(true);
    try {
      const subjectIds = Array.from(selectedSubjects);
      const response = await api.post("/admin/subjects/bulk-delete", {
        subjectIds,
      });
      toast.success(response.message || "Subjects deleted successfully");
      loadSubjects();
    } catch (error) {
      toast.error(error.error || "Bulk delete failed");
    } finally {
      setBulkActionLoading(false);
    }
  };



  const columns = [
    {
      header: (
        <input
          type="checkbox"
          checked={
            selectedSubjects.size === subjects.length && subjects.length > 0
          }
          onChange={toggleSelectAll}
          className="rounded border-border text-primary focus:ring-primary"
        />
      ),
      render: (subject) => (
        <input
          type="checkbox"
          checked={selectedSubjects.has(subject._id)}
          onChange={() => toggleSelectSubject(subject._id)}
          className="rounded border-border text-primary focus:ring-primary"
        />
      ),
      sortable: false,
    },
    {
      header: "Code",
      accessor: "code",
      sortable: true,
      render: (subject) => <span className="font-mono">{subject.code}</span>,
    },
    {
      header: "Name",
      accessor: "name",
      sortable: true,
      render: (subject) => <span className="font-medium">{subject.name}</span>,
    },
    {
      header: "Description",
      accessor: "description",
      sortable: true,
      render: (subject) => (
        <span className="opacity-80">{subject.description || "-"}</span>
      ),
    },
    {
      header: "Tags",
      sortable: false,
      render: (subject) => (
        <div className="flex flex-wrap gap-1">
          {subject.tags?.map((tag, idx) => (
            <Badge
              key={idx}
              variant="primary"
              label={tag}
              size="small"
              className="font-medium"
            />
          ))}
        </div>
      ),
    },
    {
      header: "Actions",
      sortable: false,
      render: (subject) => (
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={() => startEdit(subject)}>
            Edit
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={() => handleDelete(subject._id)}
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader
        title="Subject Management"
        action={
          <Button onClick={() => setShowCreateModal(true)}>
            Create Subject
          </Button>
        }
      />

      <FilterPanel>
        <div className="md:col-span-4">
          <Input
            label="Search"
            type="text"
            placeholder="Search by name, code, or description..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </FilterPanel>

      {selectedSubjects.size > 0 && (
        <div className="bg-primary/10 border border-primary/40 rounded-lg p-4 mb-4 flex items-center justify-between">
          <span className="text-sm font-medium text-primary">
            {selectedSubjects.size} subject
            {selectedSubjects.size > 1 ? "s" : ""} selected
          </span>
          <div className="flex gap-2">
            <Button
              variant="danger"
              size="sm"
              onClick={handleBulkDelete}
              disabled={bulkActionLoading}
            >
              Delete Selected
            </Button>
          </div>
        </div>
      )}

      <Table
        columns={columns}
        data={subjects.map((subject) => ({ ...subject, id: subject._id }))}
        loading={loading}
        emptyMessage="No subjects found"
      />

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card padding="p-6" className="max-w-md w-full">
            <h2 className="text-xl font-bold mb-4 text-text">Create Subject</h2>
            <form onSubmit={handleCreate}>
              <div className="space-y-4">
                <Input
                  label="Code"
                  type="text"
                  required
                  value={formData.code}
                  onChange={(e) =>
                    setFormData({ ...formData, code: e.target.value })
                  }
                  placeholder="MATH101"
                />
                <Input
                  label="Name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="Mathematics"
                />
                <div>
                  <label className="block text-sm font-medium text-text opacity-80 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-border bg-surface text-text rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                    rows="3"
                  />
                </div>
                <Input
                  label="Tags (comma-separated)"
                  type="text"
                  value={formData.tags}
                  onChange={(e) =>
                    setFormData({ ...formData, tags: e.target.value })
                  }
                  placeholder="math, algebra, calculus"
                />
              </div>
              <div className="mt-6 flex gap-2">
                <Button type="submit" className="flex-1">
                  Create
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowCreateModal(false);
                    setFormData({
                      code: "",
                      name: "",
                      description: "",
                      tags: "",
                    });
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      {/* Edit Modal */}
      {editingSubject && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card padding="p-6" className="max-w-md w-full">
            <h2 className="text-xl font-bold mb-4 text-text">Edit Subject</h2>
            <form onSubmit={handleUpdate}>
              <div className="space-y-4">
                <Input
                  label="Code"
                  type="text"
                  required
                  value={formData.code}
                  onChange={(e) =>
                    setFormData({ ...formData, code: e.target.value })
                  }
                />
                <Input
                  label="Name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                />
                <div>
                  <label className="block text-sm font-medium text-text opacity-80 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-border bg-surface text-text rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                    rows="3"
                  />
                </div>
                <Input
                  label="Tags"
                  type="text"
                  value={formData.tags}
                  onChange={(e) =>
                    setFormData({ ...formData, tags: e.target.value })
                  }
                />
              </div>
              <div className="mt-6 flex gap-2">
                <Button type="submit" variant="secondary" className="flex-1">
                  Update
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setEditingSubject(null);
                    setFormData({
                      code: "",
                      name: "",
                      description: "",
                      tags: "",
                    });
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}

    </div>
  );
}

export default AdminSubjects;
